import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";

import { PostHogProvider } from "posthog-js/react";
import { DefaultCatchBoundary } from "./components/DefaultCatchBoundary";
import { NotFound } from "./components/NotFound";
import { routeTree } from "./routeTree.gen";

export function getRouter() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				// Default stale time for queries
				staleTime: 1000 * 60, // 1 minute
				// Retry failed requests
				retry: (failureCount, error) => {
					// Don't retry on 4xx errors
					if (error instanceof Error && error.message.includes("4")) {
						return false;
					}
					return failureCount < 3;
				},
				// Refetch on window focus
				refetchOnWindowFocus: true,
			},
			mutations: {
				// Retry mutations once
				retry: 1,
			},
		},
	});

	const router = createRouter({
		routeTree,
		defaultPreload: "intent",
		defaultPreloadStaleTime: 0,
		defaultPendingMinMs: 200,
		defaultPendingMs: 500,
		context: { queryClient },
		scrollRestoration: true,
		defaultNotFoundComponent: () => <NotFound />,
		defaultErrorComponent: DefaultCatchBoundary,

		Wrap: ({ children }) => (
			<PostHogProvider
				apiKey={(import.meta as any).env.VITE_PUBLIC_POSTHOG_KEY}
				options={{
					api_host: (import.meta as any).env.VITE_PUBLIC_POSTHOG_HOST,
					capture_exceptions: true, // This enables capturing exceptions using Error Tracking
					persistence: "memory",
					// debug: (import.meta as any).env.MODE === "development",
				}}
			>
				{children}
			</PostHogProvider>
		),
	});
	setupRouterSsrQueryIntegration({
		router,
		queryClient,
	});
	return router;
}

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof createRouter>;
	}
}
